import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./VAContracts.css";

const BACKEND_URL = "";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c45d5dc5-1aa9-4fda-8179-43802ae25660/artifacts/r7bevoxt_1210928841187418.jpg";

const DOWNLOAD_URL = `${BACKEND_URL}/api/va-readiness/download`;

const COMPLIANCE_BADGES = [
  { code: "24 CFR 8", label: "Section 504 — Accessibility", note: "Documented per unit. Counter/vanity heights, lever hardware, grab-bar placement, clear-floor-space." },
  { code: "HUD-VASH", label: "Voucher-Grade Build Standard", note: "Exceeds HQS + NSPIRE thresholds. Lead-safe, mold-aware, electrical-safe, GFCI-protected." },
  { code: "VAWA", label: "Survivor-Privacy Build", note: "Reinforced bedroom-door strikes, peephole installation, smoke + CO + glass-break per unit." },
  { code: "TUTSA", label: "Trade-Secret Stamped", note: "Every document is timestamped, sandboxed, and audit-defensible." },
];

const QUIET_SALUTE_SPEC = [
  { k: "Walls", v: "SW 7029 Agreeable Grey — calming, non-stimulating, low-VOC" },
  { k: "Accent", v: "SW 9130 Evergreen Fog — muted sage, PTSD-aware hue" },
  { k: "Flooring", v: "Calm Oak LVP — slip-resistant, low-glare matte, no-threshold transitions" },
  { k: "Counters & Vanities", v: "34″ ADA-compliant height, lever handles only" },
  { k: "Hardware", v: "Brushed-nickel ADA lever pulls, architectural grab bars (design-grade, not medical)" },
  { k: "Lighting", v: "Dimmable LED only — no bare bulbs, no fluorescent flicker, soft-diffused" },
  { k: "HVAC", v: "Quiet-kit upgrade for sound-sensitive veterans" },
  { k: "Service Animal Ready", v: "Scratch-resistant LVP throughout" },
  { k: "Discreet Storage", v: "Built-in medical-equipment closet partition" },
];

export default function VAContracts() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    agency: "",
    phone: "",
    role: "",
    placement_context: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errMsg, setErrMsg] = useState("");

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.email.includes("@")) {
      setErrMsg("Please add your name and a valid email.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrMsg("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/va-readiness/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Submission failed.");
      }
      setStatus("sent");
    } catch (err) {
      setErrMsg(err.message || "Submission failed. Please call us directly.");
      setStatus("error");
    }
  };

  return (
    <div className="vac-page" data-testid="va-contracts-page">
      <nav className="vac-nav">
        <Link to="/" className="vac-nav-logo">
          <img src={LOGO_URL} alt="Moovin Forward" />
        </Link>
        <Link to="/" className="vac-nav-back" data-testid="va-back-home">← Home</Link>
      </nav>

      {/* Hero */}
      <header className="vac-hero">
        <div className="vac-hero-band" aria-hidden="true" />
        <div className="vac-hero-inner">
          <p className="vac-eyebrow" data-testid="va-hero-eyebrow">VA · HUD-VASH · Section 504 · VAWA</p>
          <h1 className="vac-h1" data-testid="va-hero-title">
            Honoring the men and women who served.<br />
            <span className="vac-h1-accent">Designed to follow every guideline.</span>
          </h1>
          <p className="vac-lede" data-testid="va-hero-lede">
            Moovin Forward LLC delivers interior unit stabilization for federally-assisted placements
            with documented Section 504 accessibility, HUD-VASH voucher-grade build standards, and
            VAWA survivor-privacy hardening — flat-fee, audit-defensible, TUTSA-stamped.
          </p>
          <div className="vac-hero-cta">
            <a
              href={DOWNLOAD_URL}
              className="vac-btn vac-btn-primary"
              data-testid="va-download-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              ⬇ Download Readiness PDF
            </a>
            <a href="#vac-inquiry" className="vac-btn vac-btn-ghost" data-testid="va-jump-inquiry">
              Request a 15-min Call
            </a>
          </div>
          <p className="vac-hero-fine" data-testid="va-hero-fine">
            No login. No gating. Built for contracting officers and HUD-VASH case managers
            who need to verify scope in under five minutes.
          </p>
        </div>
      </header>

      {/* Firm Scope */}
      <section className="vac-section" data-testid="va-scope-section">
        <div className="vac-section-inner">
          <div className="vac-section-head">
            <span className="vac-section-num">01</span>
            <h2 className="vac-h2">Firm Scope — TREC-Defensible</h2>
          </div>
          <p className="vac-body">
            Moovin Forward LLC is a Texas-based, woman-of-color-owned interior unit stabilization firm.
            Our scope is strictly limited to: paint, flooring, fixtures, hardware, design curation,
            sourced-materials procurement, deep clean, and inspection-ready handoff. We do
            <strong> not </strong>broker, list, manage, or lease real estate. All work is performed
            under a fixed flat-fee model with no time-and-materials drift.
          </p>
        </div>
      </section>

      {/* Quiet Salute Spec */}
      <section className="vac-section vac-section-alt" data-testid="va-spec-section">
        <div className="vac-section-inner">
          <div className="vac-section-head">
            <span className="vac-section-num">02</span>
            <h2 className="vac-h2">Quiet Salute — Disabled Veteran Package <span className="vac-spec-id">(MFC-17)</span></h2>
          </div>
          <div className="vac-spec-grid">
            {QUIET_SALUTE_SPEC.map((row, i) => (
              <div key={i} className="vac-spec-row" data-testid={`va-spec-${i}`}>
                <div className="vac-spec-key">{row.k}</div>
                <div className="vac-spec-val">{row.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Badges */}
      <section className="vac-section" data-testid="va-compliance-section">
        <div className="vac-section-inner">
          <div className="vac-section-head">
            <span className="vac-section-num">03</span>
            <h2 className="vac-h2">Federal Compliance Alignment</h2>
          </div>
          <div className="vac-badge-grid">
            {COMPLIANCE_BADGES.map((b, i) => (
              <article key={i} className="vac-badge" data-testid={`va-badge-${b.code.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                <div className="vac-badge-code">{b.code}</div>
                <h3 className="vac-badge-label">{b.label}</h3>
                <p className="vac-badge-note">{b.note}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Flat-Fee Transparency */}
      <section className="vac-section vac-section-dark" data-testid="va-fee-section">
        <div className="vac-section-inner">
          <div className="vac-section-head">
            <span className="vac-section-num vac-section-num-light">04</span>
            <h2 className="vac-h2 vac-h2-light">Flat-Fee Transparency</h2>
          </div>
          <p className="vac-body vac-body-light">
            Every unit is priced as a locked flat fee derived from a documented labor matrix
            ($65/hr installer + $50/hr prep), sourced-materials line items (curated markup),
            room-scaled overhead, and a fixed net margin. No change orders. No T&amp;M.
            50% mobilization capital on contract; balance on inspection-passed handoff.
            Anything that exceeds the historic materials cap requires written owner sign-off
            before procurement.
          </p>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="vac-section" id="vac-inquiry" data-testid="va-inquiry-section">
        <div className="vac-section-inner vac-inquiry-wrap">
          <div className="vac-inquiry-copy">
            <p className="vac-eyebrow">Direct Line</p>
            <h2 className="vac-h2">Request a 15-min Call</h2>
            <p className="vac-body">
              If you're working on a VA or HUD-VASH placement and want to confirm scope, timing,
              or accessibility specifics, send a note. Replies land with the CEO — no gatekeeper.
            </p>
            <div className="vac-direct">
              <div><strong>Shannon Ware</strong> — CEO, Moovin Forward LLC</div>
              <div>📞 (817) 381-8831</div>
              <div>✉️ <a href="mailto:hello@moovinforward.org">hello@moovinforward.org</a></div>
              <div>📍 DFW Metroplex, Texas</div>
            </div>
          </div>

          {status === "sent" ? (
            <div className="vac-form vac-form-sent" data-testid="va-form-sent">
              <h3 className="vac-form-thanks">Thank you for serving them — and for reaching out.</h3>
              <p>Your note is in. Shannon will reply personally within one business day.</p>
              <p className="vac-form-thanks-meta">In the meantime, the readiness PDF is yours to share with your team.</p>
              <a
                href={DOWNLOAD_URL}
                className="vac-btn vac-btn-primary"
                data-testid="va-form-sent-download"
                target="_blank"
                rel="noopener noreferrer"
              >
                ⬇ Download Readiness PDF
              </a>
            </div>
          ) : (
            <form className="vac-form" onSubmit={handleSubmit} data-testid="va-inquiry-form">
              <label className="vac-field">
                <span>Full Name <em>*</em></span>
                <input
                  type="text"
                  name="full_name"
                  value={form.full_name}
                  onChange={handleChange}
                  required
                  data-testid="va-form-name"
                />
              </label>
              <label className="vac-field">
                <span>Work Email <em>*</em></span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  data-testid="va-form-email"
                />
              </label>
              <label className="vac-field">
                <span>Agency / Office</span>
                <input
                  type="text"
                  name="agency"
                  value={form.agency}
                  onChange={handleChange}
                  placeholder="e.g. VA North Texas, HUD-VASH, Fort Worth Housing Solutions"
                  data-testid="va-form-agency"
                />
              </label>
              <div className="vac-field-row">
                <label className="vac-field">
                  <span>Phone</span>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="va-form-phone"
                  />
                </label>
                <label className="vac-field">
                  <span>Role</span>
                  <input
                    type="text"
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    placeholder="Case Manager, Contracting Officer, etc."
                    data-testid="va-form-role"
                  />
                </label>
              </div>
              <label className="vac-field">
                <span>What you're working on</span>
                <textarea
                  name="placement_context"
                  value={form.placement_context}
                  onChange={handleChange}
                  rows={4}
                  placeholder="e.g. Three HUD-VASH placements pending in Tarrant County, need ADA scope confirmation."
                  data-testid="va-form-context"
                />
              </label>
              {status === "error" && (
                <div className="vac-form-error" data-testid="va-form-error">{errMsg}</div>
              )}
              <button
                type="submit"
                className="vac-btn vac-btn-primary vac-btn-full"
                disabled={status === "sending"}
                data-testid="va-form-submit"
              >
                {status === "sending" ? "Sending…" : "Send Inquiry"}
              </button>
              <p className="vac-form-fine">
                Submissions land directly with the CEO. No mailing list. No automation drip.
              </p>
            </form>
          )}
        </div>
      </section>

      <footer className="vac-footer" data-testid="va-footer">
        <div className="vac-footer-inner">
          <div>
            <strong>Moovin Forward LLC</strong> — Interior Unit Stabilization · DFW Metroplex
          </div>
          <div className="vac-footer-disclaim">
            CONFIDENTIAL &amp; PROPRIETARY — Protected under Texas Uniform Trade Secrets Act (TUTSA).
            Moovin Forward LLC provides interior unit stabilization only.
            <strong> No brokerage. No property management. No leasing.</strong>
          </div>
          <div className="vac-footer-links">
            <Link to="/" data-testid="va-footer-home">Home</Link>
            <Link to="/privacy" data-testid="va-footer-privacy">Privacy</Link>
            <a href="mailto:hello@moovinforward.org">hello@moovinforward.org</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
